import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MESSAGE_DIRECTIONS,
  MESSAGE_DIRECTION_LABELS,
  MESSAGE_STATUSES,
  MESSAGE_STATUS_LABELS,
  formatPhone,
  type Message,
  type Paginated,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField } from '@/components/ContextPanel';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';

const STATUS_TONE: Record<string, BadgeKind> = {
  PENDENTE: 'mute',
  ENVIADA: 'info',
  ENTREGUE: 'ok',
  LIDA: 'ok',
  FALHOU: 'bad',
};

/**
 * Tudo que saiu e que entrou pelo canal.
 *
 * Não é log de depuração: é o registro do que a empresa disse e do que
 * responderam. Quando o Diretor começar a aprovar pagamento por WhatsApp, esta
 * tela é a prova — por isso mostra a mensagem inteira e o motivo da falha, não
 * só um "erro".
 */
export function MensagensPage() {

  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['messages', { search, direction, status, page }],
    queryFn: () =>
      api.get<Paginated<Message>>('/messages', {
        search: search || undefined,
        direction: direction || undefined,
        status: status || undefined,
        page,
        perPage: 25,
      }),
    // O operador abre esta tela logo depois de mandar, para ver se chegou.
    refetchInterval: 20_000,
  });

  const message = listQuery.data?.data.find((row) => row.id === selectedId) ?? null;

  const columns: Array<Column<Message>> = [
    {
      key: 'createdAt',
      label: 'Quando',
      mono: true,
      width: 150,
      render: (row) => formatDateTime(row.createdAt),
    },
    {
      key: 'direction',
      label: 'Sentido',
      width: 96,
      render: (row) => MESSAGE_DIRECTION_LABELS[row.direction],
    },
    {
      key: 'peerPhone',
      label: 'Quem',
      render: (row) => (
        <div>
          <div>{row.peerName ?? formatPhone(row.peerPhone)}</div>
          {row.peerName && (
            <div className="mono" style={{ fontSize: 11.5, color: T.inkFaint, marginTop: 1 }}>
              {formatPhone(row.peerPhone)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'body',
      label: 'Mensagem',
      render: (row) => (
        <span title={row.body}>{row.body.length > 48 ? `${row.body.slice(0, 48)}…` : row.body}</span>
      ),
    },
    {
      key: 'status',
      label: 'Situação',
      width: 120,
      render: (row) => (
        <StatusBadge kind={STATUS_TONE[row.status] ?? 'mute'}>
          {MESSAGE_STATUS_LABELS[row.status]}
        </StatusBadge>
      ),
    },
  ];

  const hasFilters = Boolean(search || direction || status);

  return (
    <>
      <WorkspaceHeader
        title="Mensagens"
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar pelo texto ou pelo número…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'direction',
              label: 'Sentido',
              value: direction,
              options: MESSAGE_DIRECTIONS.map((value) => ({
                value,
                label: MESSAGE_DIRECTION_LABELS[value],
              })),
              onChange: (value) => {
                setDirection(value);
                setPage(1);
              },
            },
            {
              key: 'status',
              label: 'Situação',
              value: status,
              options: MESSAGE_STATUSES.map((value) => ({
                value,
                label: MESSAGE_STATUS_LABELS[value],
              })),
              onChange: (value) => {
                setStatus(value);
                setPage(1);
              },
            },
          ]}
        />

        {listQuery.isPending && <TableSkeleton columns={5} />}
        {listQuery.isError && (
          <ErrorState
            message={errorMessage(listQuery.error)}
            onRetry={() => void listQuery.refetch()}
          />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Mensagens"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setDirection('');
                    setStatus('');
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhuma mensagem ainda"
                  description="Configure o canal em Agentes › Canal e mande um teste para conferir que chega."
                />
              )
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={message ? MESSAGE_DIRECTION_LABELS[message.direction] : 'Carregando…'}
        subtitle={message ? formatPhone(message.peerPhone) : undefined}
        tabs={['Mensagem']}
      >
        {() => {
          if (!message) return <p className="muted">Não foi possível carregar.</p>;
          return (
            <>
              <ReadField
                label="Situação"
                value={
                  <StatusBadge kind={STATUS_TONE[message.status] ?? 'mute'}>
                    {MESSAGE_STATUS_LABELS[message.status]}
                  </StatusBadge>
                }
              />
              <ReadField label="Quem" value={message.peerName ?? formatPhone(message.peerPhone)} />
              {message.templateName && (
                <ReadField label="Modelo" value={message.templateName} mono />
              )}
              <ReadField label="Criada em" value={formatDateTime(message.createdAt)} mono />
              <ReadField
                label="Enviada em"
                value={message.sentAt ? formatDateTime(message.sentAt) : '—'}
                mono
              />
              <ReadField
                label="Entregue em"
                value={message.deliveredAt ? formatDateTime(message.deliveredAt) : '—'}
                mono
              />
              <ReadField
                label="Lida em"
                value={message.readAt ? formatDateTime(message.readAt) : '—'}
                mono
              />

              {message.errorMessage && (
                <div className="login__alert" role="alert" style={{ marginTop: 14 }}>
                  {message.errorMessage}
                </div>
              )}

              <div className="card" style={{ padding: 12, marginTop: 14 }}>
                <div style={{ fontSize: 11.5, color: T.inkFaint, marginBottom: 6 }}>Conteúdo</div>
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{message.body}</div>
              </div>
            </>
          );
        }}
      </ContextPanel>
    </>
  );
}
